import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { AlertTriangle, Calendar, CalendarClock, PartyPopper, RefreshCw } from 'lucide-react';
import { addDays, isBefore, startOfDay } from 'date-fns';

interface PanelLesson {
  id: string;
  title: string;
  completed?: boolean;
  mandatory?: boolean;
  target_levels?: number[] | null;
}

interface PanelChapter {
  id: string;
  title: string;
  week?: number;
  lessons: PanelLesson[];
}

interface SelfPacedRoutePanelProps {
  cohortId: string;
  planStartAt: string | null;
  chapters: PanelChapter[];
  onOpenLesson?: (lessonId: string) => void;
}

type ChapterState = 'completed' | 'current' | 'pending';

export function SelfPacedRoutePanel({ cohortId, planStartAt, chapters }: SelfPacedRoutePanelProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [replanning, setReplanning] = useState(false);

  const planStart = planStartAt ? new Date(planStartAt) : null;

  const mandatoryTotal = chapters.reduce((acc, ch) => acc + ch.lessons.filter(l => l.mandatory).length, 0);
  const mandatoryDone = chapters.reduce((acc, ch) => acc + ch.lessons.filter(l => l.mandatory && l.completed).length, 0);
  const mandatoryPct = mandatoryTotal ? Math.round((mandatoryDone / mandatoryTotal) * 100) : 0;

  const rows = useMemo(() => {
    const today = startOfDay(new Date());
    let currentAssigned = false;

    return chapters.map((ch, index) => {
      const mandatory = ch.lessons.filter(l => l.mandatory);
      const pool = mandatory.length > 0 ? mandatory : ch.lessons;
      const done = pool.filter(l => l.completed).length;
      const isDone = pool.length > 0 && done === pool.length;

      let state: ChapterState = 'pending';
      if (isDone) {
        state = 'completed';
      } else if (!currentAssigned) {
        state = 'current';
        currentAssigned = true;
      }

      const weekOffset = ch.week ?? index + 1;
      const suggestedDate = planStart ? addDays(planStart, weekOffset * 7) : null;
      const overdue = !isDone && !!suggestedDate && isBefore(suggestedDate, today);

      return { chapter: ch, state, suggestedDate, overdue, done, total: pool.length };
    });
  }, [chapters, planStartAt]);

  const overdueCount = rows.filter(r => r.overdue).length;
  const routeCompleted = mandatoryTotal > 0 && mandatoryDone === mandatoryTotal;

  const currentWeek = useMemo(() => {
    const idx = rows.findIndex(r => r.state === 'current');
    if (idx === -1) return null;
    return rows[idx].chapter.week ?? idx + 1;
  }, [rows]);

  const handleReplan = async () => {
    setReplanning(true);
    const { error } = await supabase.rpc('replan_self_paced_route' as any, { _cohort_id: cohortId });
    setReplanning(false);
    if (error) {
      toast({ title: 'No pudimos replanificar', description: error.message, variant: 'destructive' });
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ['all-cohort-enrollments'] });
    toast({ title: 'Plan reiniciado', description: 'Tus fechas sugeridas se recalcularon desde hoy.' });
  };

  return (
    <div className="space-y-4">
      {/* Progreso de obligatorios */}
      <div className="bg-card border border-border rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-primary" />
            <span className="font-semibold text-foreground text-sm">Tu plan de autoaprendizaje</span>
          </div>
          <div className="flex items-center gap-3">
            {currentWeek && (
              <div className="flex items-center gap-2 px-2.5 py-1 rounded-lg bg-primary/10 border border-primary/20">
                <Calendar className="w-3.5 h-3.5 text-primary" />
                <span className="text-xs font-medium text-foreground">Semana {currentWeek}</span>
              </div>
            )}
            <span className="text-xs text-muted-foreground">
              {mandatoryDone} de {mandatoryTotal} contenidos obligatorios
            </span>
          </div>
        </div>
        <Progress value={mandatoryPct} className="h-2" />
      </div>

      {/* Banner de atraso */}
      {overdueCount > 0 && !routeCompleted && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground">
              Vas atrasado en {overdueCount} {overdueCount === 1 ? 'capítulo' : 'capítulos'}
            </p>
            <p className="text-xs text-muted-foreground">
              Puedes replanificar tus fechas sugeridas para empezar de nuevo desde hoy, sin perder tu progreso.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={handleReplan} disabled={replanning}>
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${replanning ? 'animate-spin' : ''}`} />
            Replanificar
          </Button>
        </div>
      )}

      {/* Ruta completada */}
      {routeCompleted && (
        <div className="flex items-center gap-3 rounded-xl border border-primary/40 bg-primary/10 p-4">
          <PartyPopper className="w-5 h-5 text-primary flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-foreground">¡Ruta completada!</p>
            <p className="text-xs text-muted-foreground">
              Completaste todo el contenido obligatorio. Sigue explorando el contenido opcional a tu ritmo.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
